/**
 * Improvement Loop Configuration Panel
 *
 * Shows configuration options before starting the multi-persona improvement loop:
 * persona selection, max iterations, and start/cancel buttons.
 *
 * Story: 24.4 - Multi-Persona Content Improvement Engine
 */

import type { MultiPersonaLoopConfig } from '../hooks/useImprovementLoop';
import { MultiPersonaSelector } from './MultiPersonaSelector';

interface ImprovementConfigPanelProps {
  config: MultiPersonaLoopConfig;
  onConfigChange: (config: Partial<MultiPersonaLoopConfig>) => void;
  onStart: () => void;
  onCancel: () => void;
}

export function ImprovementConfigPanel({
  config,
  onConfigChange,
  onStart,
  onCancel,
}: ImprovementConfigPanelProps) {
  return (
    <div className="bg-white border border-gray-300 rounded-lg p-4">
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Improvement Loop Settings</h3>

      <div className="space-y-4">
        {/* Multi-Persona Selector */}
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-2">Eval Personas</label>
          <MultiPersonaSelector
            selectedPersonaIds={config.selectedPersonaIds}
            onSelectionChange={(ids) => onConfigChange({ selectedPersonaIds: ids })}
          />
        </div>

        {/* Max Iterations */}
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1">Max Iterations</label>
          <select
            value={config.maxIterations}
            onChange={(e) => onConfigChange({ maxIterations: Number(e.target.value) })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {Array.from({ length: 9 }, (_, i) => i + 2).map((n) => (
              <option key={n} value={n}>
                {n} iterations
              </option>
            ))}
          </select>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-2">
          <button
            onClick={onStart}
            disabled={config.selectedPersonaIds.length === 0}
            className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            Start ({config.selectedPersonaIds.length} persona
            {config.selectedPersonaIds.length !== 1 ? 's' : ''})
          </button>
          <button
            onClick={onCancel}
            className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors font-medium"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Loop Progress Display
// ============================================================================

interface LoopProgressProps {
  currentIteration: number;
  maxIterations: number;
  status: string;
  simulatingPersonaIndex: number;
  totalPersonas: number;
  onCancel: () => void;
}

const STATUS_LABELS: Record<string, string> = {
  simulating: 'Simulating & judging personas...',
  judging: 'Judging simulations...',
  summarizing: 'Summarizing judge opinions...',
  improving: 'Applying improvements...',
};

export function LoopProgress({
  currentIteration,
  maxIterations,
  status,
  simulatingPersonaIndex,
  totalPersonas,
  onCancel,
}: LoopProgressProps) {
  const isBaseline = currentIteration === 0;
  const displayLabel = isBaseline ? 'Baseline' : `Iteration ${currentIteration} / ${maxIterations}`;

  return (
    <div className="bg-white border border-blue-200 rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-gray-800">{displayLabel}</h3>
        <button
          onClick={onCancel}
          className="px-3 py-1 text-xs text-red-600 hover:text-red-800 border border-red-200 rounded-md hover:bg-red-50 transition-colors"
        >
          Cancel
        </button>
      </div>

      <div className="flex items-center gap-2">
        {status !== 'complete' && (
          <div className="animate-spin h-4 w-4 border-2 border-blue-600 border-t-transparent rounded-full" />
        )}
        <span className="text-sm text-gray-700">{STATUS_LABELS[status] || status}</span>
      </div>

      {status === 'simulating' && totalPersonas > 1 && (
        <p className="text-xs text-gray-500 mt-1">
          {simulatingPersonaIndex} of {totalPersonas} completed
        </p>
      )}

      {/* Progress bar */}
      <div className="mt-3 w-full bg-gray-200 rounded-full h-1.5">
        <div
          className="bg-blue-600 h-1.5 rounded-full transition-all duration-500"
          style={{ width: `${Math.max(5, (currentIteration / maxIterations) * 100)}%` }}
        />
      </div>
    </div>
  );
}
